import { cn } from '@/lib/utils'
import { Badge } from './Badge'
import type { PackFinding } from '@/lib/industry-packs/types'

interface ComplianceCardProps {
  industry: string
  industryLabel: string
  findings: PackFinding[]
  maxSeverity: 'clean' | 'info' | 'warn' | 'block'
  plainLanguageScore: number
  className?: string
}

const SEVERITY_CONFIG = {
  block: { label: 'Blocking', badgeVariant: 'error' as const, bg: 'bg-red-50 border-red-200', icon: '⊘', iconColor: 'text-red-500' },
  warn: { label: 'Warnings', badgeVariant: 'warning' as const, bg: 'bg-amber-50 border-amber-200', icon: '⚠', iconColor: 'text-uncertainty-amber' },
  info: { label: 'Notes', badgeVariant: 'info' as const, bg: 'bg-blue-50 border-blue-200', icon: 'ℹ', iconColor: 'text-blue-500' },
  clean: { label: 'Clean', badgeVariant: 'success' as const, bg: 'bg-green-50 border-green-200', icon: '✓', iconColor: 'text-validation-green' },
}

function ScoreMeter({ score, label }: { score: number; label: string }) {
  const color = score >= 70 ? 'bg-validation-green' : score >= 40 ? 'bg-uncertainty-amber' : 'bg-red-400'
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>{label}</span>
        <span className="font-medium text-deep-ink">{score}/100</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${score}%` }} />
      </div>
    </div>
  )
}

function FindingRow({ finding }: { finding: PackFinding }) {
  const config = SEVERITY_CONFIG[finding.severity as keyof typeof SEVERITY_CONFIG] ?? SEVERITY_CONFIG.info
  return (
    <div className="bg-white rounded-lg p-3 border border-border-gray">
      <div className="flex items-start gap-2">
        <span className={cn('text-sm mt-0.5 shrink-0', config.iconColor)}>{config.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant={config.badgeVariant} size="sm">{config.label}</Badge>
            <code className="text-xs text-slate-400">{finding.rule_id}</code>
          </div>
          <p className="text-xs text-slate-700">{finding.message}</p>
          {finding.cited_authority && (
            <p className="text-xs text-slate-400 mt-1 italic">{finding.cited_authority}</p>
          )}
          {finding.suggested_rewrite && (
            <div className="mt-2 bg-soft-panel rounded p-2">
              <p className="text-xs font-medium text-slate-500 mb-0.5">Suggested rewrite</p>
              <p className="text-xs text-slate-600">{finding.suggested_rewrite}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function ComplianceCard({
  industry,
  industryLabel,
  findings,
  maxSeverity,
  plainLanguageScore,
  className,
}: ComplianceCardProps) {
  const config = SEVERITY_CONFIG[maxSeverity]
  const blockCount = findings.filter(f => f.severity === 'block').length
  const warnCount = findings.filter(f => f.severity === 'warn').length
  const infoCount = findings.filter(f => f.severity === 'info').length

  return (
    <div className={cn('rounded-xl border p-4 space-y-4', config.bg, className)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={cn('text-base', config.iconColor)}>{config.icon}</span>
            <h3 className="text-sm font-medium text-deep-ink">{industryLabel} Compliance</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {blockCount > 0 && (
              <Badge variant="error" size="sm">{blockCount} blocking</Badge>
            )}
            {warnCount > 0 && (
              <Badge variant="warning" size="sm">{warnCount} warning{warnCount !== 1 ? 's' : ''}</Badge>
            )}
            {infoCount > 0 && (
              <Badge variant="info" size="sm">{infoCount} note{infoCount !== 1 ? 's' : ''}</Badge>
            )}
            {maxSeverity === 'clean' && (
              <Badge variant="success" size="sm">No issues found</Badge>
            )}
          </div>
        </div>
        <div className="w-36">
          <ScoreMeter score={plainLanguageScore} label="Plain language" />
        </div>
      </div>

      {/* Findings */}
      {findings.length > 0 && (
        <div className="space-y-2">
          {findings.map((f, i) => (
            <FindingRow key={`${f.rule_id}-${i}`} finding={f} />
          ))}
        </div>
      )}

      {maxSeverity === 'clean' && (
        <p className="text-xs text-validation-green text-center py-1">
          Stimulus passed all {industryLabel.toLowerCase()} compliance rules.
        </p>
      )}
    </div>
  )
}
